import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, resolveImage } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Save, Image as ImageIcon, RotateCcw } from "lucide-react";
import ImagePicker from "./ImagePicker";

/** Group definitions: every editable field of /api/content arranged by section. */
const SECTIONS = [
  {
    title: "Encabezado del sitio (header)",
    fields: [
      { key: "brand_name", label: "Nombre de marca", type: "text" },
      { key: "brand_subtitle", label: "Subtítulo (manuscrito)", type: "text" },
      { key: "header_cta_label", label: "Texto del botón CTA del header", type: "text" },
    ],
  },
  {
    title: "Pantalla de intro",
    fields: [
      { key: "intro_text", label: "Texto del portal de bienvenida", type: "text" },
    ],
  },
  {
    title: "Hero principal",
    fields: [
      { key: "hero_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "hero_title_line_1", label: "Título · línea 1", type: "text" },
      { key: "hero_title_line_2", label: "Título · línea 2 (resaltada, cursiva)", type: "text" },
      { key: "hero_title_line_3", label: "Título · línea 3", type: "text" },
      { key: "hero_description", label: "Descripción", type: "textarea" },
      { key: "hero_cta_primary", label: "Botón principal (texto)", type: "text" },
      { key: "hero_cta_secondary", label: "Botón secundario (texto)", type: "text" },
      { key: "hero_stat1_value", label: "Estadística 1 · valor", type: "text" },
      { key: "hero_stat1_label", label: "Estadística 1 · etiqueta", type: "text" },
      { key: "hero_stat2_value", label: "Estadística 2 · valor", type: "text" },
      { key: "hero_stat2_label", label: "Estadística 2 · etiqueta", type: "text" },
      { key: "hero_stat3_value", label: "Estadística 3 · valor", type: "text" },
      { key: "hero_stat3_label", label: "Estadística 3 · etiqueta", type: "text" },
      { key: "hero_image_far", label: "Imagen capa lejana (parallax)", type: "image" },
      { key: "hero_image_mid", label: "Imagen capa media (parallax)", type: "image" },
      { key: "hero_image_front", label: "Imagen capa frontal (parallax)", type: "image" },
      { key: "hero_portal_image", label: "Imagen del portal arqueado", type: "image" },
      { key: "hero_portal_overline", label: "Portal · 'Bienvenido a'", type: "text" },
      { key: "hero_portal_title", label: "Portal · título", type: "text" },
      { key: "hero_portal_fairy", label: "Portal · frase manuscrita", type: "text" },
    ],
  },
  {
    title: "Sobre la comunidad",
    fields: [
      { key: "about_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "about_title_1", label: "Título · primera parte", type: "text" },
      { key: "about_title_fairy", label: "Palabra mágica (manuscrita)", type: "text" },
      { key: "about_description", label: "Descripción", type: "textarea" },
      { key: "distance_pextlan", label: "Distancia a Cañones de Pextlán (texto)", type: "text" },
      { key: "distance_pextlan_label", label: "Etiqueta Cañones de Pextlán", type: "text" },
      { key: "distance_xico", label: "Distancia a Xico (texto)", type: "text" },
      { key: "distance_xico_label", label: "Etiqueta Pueblo mágico de Xico", type: "text" },
      { key: "about_caption", label: "Cita inferior ('Sin vallas...')", type: "textarea" },
      { key: "about_image_1", label: "Imagen flotante 1", type: "image" },
      { key: "about_image_2", label: "Imagen flotante 2", type: "image" },
      { key: "about_image_3", label: "Imagen flotante 3", type: "image" },
    ],
  },
  {
    title: "Sección de preventa",
    fields: [
      { key: "presale_subtitle", label: "Subtítulo / badge promo", type: "text" },
      { key: "presale_title", label: "Título grande", type: "text" },
      { key: "presale_description", label: "Descripción", type: "textarea" },
      { key: "presale_footer_note", label: "Nota al pie de la sección preventa", type: "textarea" },
    ],
  },
  {
    title: "Sección de lotes (los 12)",
    fields: [
      { key: "lotes_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "lotes_title_1", label: "Título · primera parte", type: "text" },
      { key: "lotes_title_fairy", label: "Palabra mágica (manuscrita)", type: "text" },
      { key: "lotes_title_2", label: "Título · última parte", type: "text" },
      { key: "lotes_description", label: "Descripción", type: "textarea" },
    ],
  },
  {
    title: "Sección del mapa",
    fields: [
      { key: "map_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "map_title_1", label: "Título · primera parte", type: "text" },
      { key: "map_title_fairy", label: "Palabra mágica (manuscrita)", type: "text" },
      { key: "map_description", label: "Descripción", type: "textarea" },
      { key: "map_stamp", label: "Sello inferior del mapa", type: "text" },
    ],
  },
  {
    title: "Sección de amenidades",
    fields: [
      { key: "amenities_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "amenities_title_1", label: "Título principal", type: "text" },
      { key: "amenities_title_fairy", label: "Subtítulo (manuscrito)", type: "text" },
      { key: "amenities_description", label: "Descripción", type: "textarea" },
    ],
  },
  {
    title: "Galería · Imágenes y videos",
    fields: [
      { key: "gallery_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "gallery_title_1", label: "Título · primera parte", type: "text" },
      { key: "gallery_title_fairy", label: "Palabra mágica (manuscrita)", type: "text" },
      { key: "gallery_description", label: "Descripción", type: "textarea" },
    ],
  },
  {
    title: "Sección de confianza",
    fields: [
      { key: "trust_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "trust_title_1", label: "Título · primera parte", type: "text" },
      { key: "trust_title_fairy", label: "Palabra mágica (manuscrita)", type: "text" },
      { key: "trust_seal_overline", label: "Sello · eyebrow", type: "text" },
      { key: "trust_seal_title", label: "Sello · título", type: "text" },
      { key: "trust_seal_fairy", label: "Sello · frase manuscrita", type: "text" },
    ],
  },
  {
    title: "Sección de testimonios",
    fields: [
      { key: "testimonials_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "testimonials_title_1", label: "Título · primera parte", type: "text" },
      { key: "testimonials_title_fairy", label: "Palabra mágica (manuscrita)", type: "text" },
    ],
  },
  {
    title: "Sección de contacto",
    fields: [
      { key: "contact_overline", label: "Línea superior (eyebrow)", type: "text" },
      { key: "contact_title_1", label: "Título · primera parte", type: "text" },
      { key: "contact_title_fairy", label: "Palabra mágica (manuscrita)", type: "text" },
      { key: "contact_description", label: "Descripción", type: "textarea" },
      { key: "contact_bullets", label: "Lista de beneficios (uno por línea)", type: "textarea" },
      { key: "contact_submit_label", label: "Texto del botón enviar", type: "text" },
      { key: "contact_disclaimer", label: "Aviso bajo el botón", type: "text" },
    ],
  },
  {
    title: "Pie de página (footer)",
    fields: [
      { key: "footer_about", label: "Descripción debajo del logo", type: "textarea" },
      { key: "footer_location_title", label: "Encabezado columna Ubicación", type: "text" },
      { key: "footer_location_items", label: "Items de ubicación (uno por línea)", type: "textarea" },
      { key: "footer_contact_title", label: "Encabezado columna Contacto", type: "text" },
      { key: "footer_contact_hours", label: "Horario de contacto", type: "text" },
      { key: "footer_contact_extra", label: "Línea extra de contacto", type: "text" },
      { key: "footer_copyright", label: "Texto de copyright", type: "text" },
      { key: "footer_admin_link", label: "Etiqueta enlace admin", type: "text" },
    ],
  },
  {
    title: "WhatsApp",
    fields: [
      { key: "whatsapp_default_message", label: "Mensaje pre-llenado del botón flotante", type: "textarea" },
      { key: "whatsapp_fab_label", label: "Etiqueta del botón flotante", type: "text" },
    ],
  },
];

export default function ContentEditor() {
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // key being picked

  const load = async () => {
    try {
      const res = await api.get("/content");
      setForm(res.data);
      setOriginal(res.data);
      setDirty(new Set());
    } catch {
      toast.error("No se pudo cargar el contenido");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty((d) => {
      const n = new Set(d);
      if (v !== original[k]) n.add(k);
      else n.delete(k);
      return n;
    });
  };

  const resetField = (k) => {
    setForm((f) => ({ ...f, [k]: original[k] }));
    setDirty((d) => {
      const n = new Set(d);
      n.delete(k);
      return n;
    });
  };

  const save = async () => {
    if (dirty.size === 0) {
      toast.info("Nada que guardar");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      dirty.forEach((k) => (payload[k] = form[k]));
      await api.put("/content", payload);
      toast.success(`Guardados ${dirty.size} campos`);
      setOriginal({ ...original, ...payload });
      setDirty(new Set());
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3 sticky top-20 z-10 bg-[var(--c-bg-deep)]/80 backdrop-blur py-3 -mx-2 px-2 rounded-xl">
        <div>
          <h2 className="font-serif text-3xl text-[var(--c-text)]">Editor de contenido</h2>
          <p className="text-sm text-[var(--c-text)]/65 mt-1">
            Edita todos los textos e imágenes del sitio. Cambios marcados en dorado.
          </p>
        </div>
        <Button
          data-testid="save-content"
          onClick={save}
          disabled={saving || dirty.size === 0}
          className="bg-gradient-to-r from-[var(--c-firefly-gold)] to-[var(--c-bio-green)] hover:opacity-90 text-[#0B1A0F] font-semibold rounded-full disabled:opacity-40"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Guardando..." : `Guardar ${dirty.size > 0 ? `(${dirty.size})` : ""}`}
        </Button>
      </div>

      {SECTIONS.map((sec) => (
        <div
          key={sec.title}
          className="rounded-2xl border border-[var(--c-bio-green)]/15 bg-[var(--c-bg-forest)]/60 backdrop-blur overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-[var(--c-bio-green)]/15 bg-[var(--c-bg-deep)]/50">
            <h3 className="font-serif text-xl text-[var(--c-firefly-gold)]">{sec.title}</h3>
          </div>
          <div className="p-5 grid sm:grid-cols-2 gap-4">
            {sec.fields.map((f) => {
              const isDirty = dirty.has(f.key);
              const value = form[f.key] ?? "";
              return (
                <div
                  key={f.key}
                  className={`${f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}`}
                  data-testid={`content-field-${f.key}`}
                >
                  <div className="flex items-center justify-between">
                    <label className="text-xs uppercase tracking-widest text-[var(--c-bio-green)] flex items-center gap-1.5">
                      {f.label}
                      {isDirty && (
                        <span
                          className="text-[9px] tracking-normal px-1.5 py-0.5 rounded-full bg-[var(--c-firefly-gold)] text-[#0B1A0F] font-semibold"
                        >
                          ●
                        </span>
                      )}
                    </label>
                    {isDirty && (
                      <button
                        type="button"
                        onClick={() => resetField(f.key)}
                        className="text-[10px] text-[var(--c-text)]/55 hover:text-[var(--c-firefly-gold)] inline-flex items-center gap-1"
                        title="Deshacer"
                      >
                        <RotateCcw className="w-3 h-3" /> deshacer
                      </button>
                    )}
                  </div>
                  {f.type === "text" && (
                    <Input
                      value={value}
                      onChange={(e) => update(f.key, e.target.value)}
                      className="mt-1.5 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl"
                    />
                  )}
                  {f.type === "textarea" && (
                    <Textarea
                      rows={3}
                      value={value}
                      onChange={(e) => update(f.key, e.target.value)}
                      className="mt-1.5 bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl"
                    />
                  )}
                  {f.type === "image" && (
                    <div className="mt-1.5 flex items-center gap-3">
                      <div className="w-28 h-20 rounded-lg overflow-hidden border border-[var(--c-bio-green)]/20 bg-[var(--c-bg-deep)] flex items-center justify-center shrink-0">
                        {value ? (
                          <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-[var(--c-text)]/40" />
                        )}
                      </div>
                      <div className="flex-1 flex flex-col gap-2">
                        <Input
                          value={value}
                          onChange={(e) => update(f.key, e.target.value)}
                          placeholder="URL externa o usa biblioteca"
                          className="bg-[var(--c-bg-deep)] border-[var(--c-bio-green)]/25 text-[var(--c-text)] rounded-xl text-xs"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setPicker(f.key)}
                          className="bg-transparent border border-[var(--c-bio-green)]/30 text-[var(--c-bio-green)] hover:bg-[var(--c-bio-green)] hover:text-[#0B1A0F]"
                        >
                          <ImageIcon className="w-3.5 h-3.5 mr-1" /> Elegir de biblioteca
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <ImagePicker
        open={!!picker}
        onOpenChange={(v) => !v && setPicker(null)}
        multiple={false}
        initialSelected={picker && form[picker] ? [form[picker]] : []}
        onConfirm={(url) => {
          if (picker) update(picker, url);
        }}
      />
    </div>
  );
}
